import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { ImpuestosSelector } from '@/components/ui/impuestos-selector'
import { ClientesMultiSelector } from '@/components/ui/clientes-multi-selector'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useCatalogoImpuestos } from '@/hooks/useCatalogoImpuestos'
import { useClientes } from '@/hooks/useClientes'
import { useObligacionRecurrenteForm } from './forms/useObligacionRecurrenteForm'
import { getFieldError } from '@/components/clientes/wizard/useClienteForm'
import {
  CATEGORIAS_OBLIGACION_OPTIONS,
  PERIODICIDADES_OBLIGACION_OPTIONS,
  COLORES_OBLIGACION,
  COLOR_OBLIGACION_LABELS,
  MODOS_ASIGNACION,
  MODO_ASIGNACION_LABELS,
  MODO_ASIGNACION_DESCRIPCIONES,
  type ObligacionRecurrenteFormValues,
  type ColorObligacion,
  type ModoAsignacion,
} from './forms/obligacionRecurrenteSchema'
import { CONDICIONES_IVA } from '@/workspaces/contable/types/faceta'
import type { ObligacionRecurrenteConClientes } from '@/services/obligacionesRecurrentesService'
import type { ImpuestoLiquidar } from '@/types/impuestos'

interface ObligacionRecurrenteFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  obligacion?: ObligacionRecurrenteConClientes | null
  onSubmit: (data: ObligacionRecurrenteFormValues) => Promise<void> | void
  isLoading?: boolean
}

const COLOR_SWATCHES: Record<ColorObligacion, string> = {
  gray: 'bg-gray-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  yellow: 'bg-yellow-500',
  green: 'bg-green-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  blue: 'bg-blue-500',
  sky: 'bg-sky-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
  purple: 'bg-purple-500',
}

export function ObligacionRecurrenteFormDialog({
  open,
  onOpenChange,
  obligacion,
  onSubmit,
  isLoading,
}: ObligacionRecurrenteFormDialogProps) {
  const isEditing = !!obligacion
  const { catalogo } = useCatalogoImpuestos()
  // Solo clientes activos: el resolver del backend descarta inactivos y, si el set
  // queda vacío, limpia el horizonte de pendientes (evita selección silenciosa inválida).
  const { data: clientes = [] } = useClientes({ estado: 'activo' })

  const { form } = useObligacionRecurrenteForm({
    defaultValues: obligacion
      ? {
          nombre: obligacion.nombre,
          descripcion: obligacion.descripcion,
          categoria: obligacion.categoria as ObligacionRecurrenteFormValues['categoria'],
          periodicidad: obligacion.periodicidad as ObligacionRecurrenteFormValues['periodicidad'],
          dia_vencimiento: obligacion.dia_vencimiento,
          mes_offset: obligacion.mes_offset,
          aplica_dia_habil: obligacion.aplica_dia_habil ?? false,
          modo_asignacion:
            (obligacion.modo_asignacion as ModoAsignacion | null) ??
            (obligacion.aplica_a_todos ? 'todos' : 'filtros'),
          condicion_iva_filtro: obligacion.condicion_iva_filtro,
          impuesto_filtro: obligacion.impuesto_filtro ?? null,
          clientes_filtro: obligacion.clientes_filtro ?? null,
          color: (obligacion.color as ObligacionRecurrenteFormValues['color']) ?? 'blue',
          activo: obligacion.activo ?? true,
        }
      : undefined,
    onSubmit: async (data) => {
      await onSubmit(data)
      onOpenChange(false)
    },
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent key={obligacion?.id ?? 'new'} className="max-w-lg max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Obligación' : 'Nueva Obligación Recurrente'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifica los datos de la obligación recurrente'
              : 'Define una obligación que genera vencimientos automáticamente'}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="flex-1 overflow-y-auto space-y-6 py-2"
        >
          {/* Seccion: Basico */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Datos básicos</h4>

            <form.Field name="nombre">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="Ej: IVA Mensual"
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="descripcion">
              {(field) => (
                <div className="space-y-2">
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Textarea
                    id="descripcion"
                    value={field.state.value ?? ''}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="Descripción opcional"
                    rows={2}
                  />
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-2 gap-4">
              <form.Field name="categoria">
                {(field) => (
                  <div className="space-y-2">
                    <Label>Categoría *</Label>
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => field.handleChange(v as ObligacionRecurrenteFormValues['categoria'])}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIAS_OBLIGACION_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="color">
                {(field) => (
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <Select
                      value={field.state.value ?? 'blue'}
                      onValueChange={(v) => field.handleChange(v as ColorObligacion)}
                    >
                      <SelectTrigger>
                        <SelectValue>
                          <div className="flex items-center gap-2">
                            <div className={`h-3 w-3 rounded-full ${COLOR_SWATCHES[(field.state.value ?? 'blue') as ColorObligacion]}`} />
                            {COLOR_OBLIGACION_LABELS[(field.state.value ?? 'blue') as ColorObligacion]}
                          </div>
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {COLORES_OBLIGACION.map((color) => (
                          <SelectItem key={color} value={color}>
                            <div className="flex items-center gap-2">
                              <div className={`h-3 w-3 rounded-full ${COLOR_SWATCHES[color]}`} />
                              {COLOR_OBLIGACION_LABELS[color]}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
            </div>
          </div>

          {/* Seccion: Vencimiento */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Vencimiento</h4>

            <form.Field name="periodicidad">
              {(field) => (
                <div className="space-y-2">
                  <Label>Periodicidad *</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) => field.handleChange(v as ObligacionRecurrenteFormValues['periodicidad'])}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      {PERIODICIDADES_OBLIGACION_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                  )}
                </div>
              )}
            </form.Field>

            <div className="grid grid-cols-2 gap-4">
              <form.Field name="dia_vencimiento">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="dia_vencimiento">Día de vencimiento *</Label>
                    <Input
                      id="dia_vencimiento"
                      type="number"
                      min={1}
                      max={31}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(Number(e.target.value))}
                      onBlur={field.handleBlur}
                    />
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                    )}
                  </div>
                )}
              </form.Field>

              <form.Field name="mes_offset">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="mes_offset">Meses de offset</Label>
                    <Input
                      id="mes_offset"
                      type="number"
                      min={0}
                      max={12}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(Number(e.target.value))}
                      onBlur={field.handleBlur}
                    />
                    <p className="text-xs text-muted-foreground">
                      Meses después del periodo (0 = mismo mes)
                    </p>
                    {field.state.meta.errors.length > 0 && (
                      <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                    )}
                  </div>
                )}
              </form.Field>
            </div>

            <form.Field name="aplica_dia_habil">
              {(field) => (
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Ajustar a día hábil</Label>
                    <p className="text-xs text-muted-foreground">
                      Si cae fin de semana/feriado, mover al siguiente hábil
                    </p>
                  </div>
                  <Switch
                    checked={field.state.value}
                    onCheckedChange={field.handleChange}
                  />
                </div>
              )}
            </form.Field>
          </div>

          {/* Seccion: Alcance */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-muted-foreground">Alcance</h4>

            <form.Field name="modo_asignacion">
              {(field) => (
                <RadioGroup
                  value={field.state.value}
                  onValueChange={(v) => field.handleChange(v as ModoAsignacion)}
                  className="space-y-2"
                >
                  {MODOS_ASIGNACION.map((modo) => (
                    <div key={modo} className="flex items-start gap-2">
                      <RadioGroupItem value={modo} id={`modo-${modo}`} className="mt-1" />
                      <div className="space-y-0.5">
                        <Label htmlFor={`modo-${modo}`} className="font-normal cursor-pointer">
                          {MODO_ASIGNACION_LABELS[modo]}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {MODO_ASIGNACION_DESCRIPCIONES[modo]}
                        </p>
                      </div>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </form.Field>

            <form.Subscribe selector={(state) => state.values.modo_asignacion}>
              {(modo) =>
                modo === 'filtros' ? (
                  <div className="space-y-4">
                    <form.Field name="condicion_iva_filtro">
                      {(field) => {
                        const selected = field.state.value ?? []
                        return (
                          <div className="space-y-2">
                            <Label>Condiciones IVA</Label>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(CONDICIONES_IVA).map(([key, label]) => (
                                <div key={key} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`iva-${key}`}
                                    checked={selected.includes(key)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        field.handleChange([...selected, key])
                                      } else {
                                        field.handleChange(selected.filter((v) => v !== key))
                                      }
                                    }}
                                  />
                                  <Label htmlFor={`iva-${key}`} className="text-sm font-normal">
                                    {label}
                                  </Label>
                                </div>
                              ))}
                            </div>
                            {field.state.meta.errors.length > 0 && (
                              <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                            )}
                          </div>
                        )
                      }}
                    </form.Field>

                    <form.Field name="impuesto_filtro">
                      {(field) => {
                        const selectedIds = field.state.value ?? []
                        const selectorValue: ImpuestoLiquidar[] = selectedIds.map((id) => {
                          const cat = catalogo?.find((c) => c.id === id)
                          return { impuesto_id: id, codigo: cat?.codigo ?? '' }
                        })
                        return (
                          <div className="space-y-2">
                            <Label>Impuestos asignados</Label>
                            <ImpuestosSelector
                              catalogo={catalogo ?? []}
                              value={selectorValue}
                              onChange={(impuestos) => {
                                field.handleChange(
                                  impuestos.length > 0
                                    ? impuestos.map((i) => i.impuesto_id)
                                    : null
                                )
                              }}
                            />
                            <p className="text-xs text-muted-foreground">
                              Solo aplica a clientes que tengan estos impuestos asignados
                            </p>
                          </div>
                        )
                      }}
                    </form.Field>

                    <p className="text-xs text-muted-foreground italic">
                      Si seleccionás ambos filtros, el cliente debe cumplir los dos (AND)
                    </p>
                  </div>
                ) : modo === 'clientes' ? (
                  <form.Field name="clientes_filtro">
                    {(field) => (
                      <div className="space-y-2">
                        <Label>Clientes específicos</Label>
                        <ClientesMultiSelector
                          clientes={clientes}
                          value={field.state.value ?? []}
                          onValueChange={(ids) => field.handleChange(ids.length > 0 ? ids : null)}
                        />
                        {field.state.meta.errors.length > 0 && (
                          <p className="text-destructive text-sm">{getFieldError(field.state.meta.errors)}</p>
                        )}
                      </div>
                    )}
                  </form.Field>
                ) : null
              }
            </form.Subscribe>
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
